import { buildSectionClassname } from './classNameUtils';
import { writeBackgroundLayersToCss } from './backgroundLayerStylesUtils';
import { writeMargin, writeMaxWidthToCss, writePadding, isStyleOverridden } from './gridLayoutStyleUtils';
import combineBreakpointStyles, { buildNodeSelector, isMediaQuery } from './combineBreakpointStyles';
import { isBackgroundLayerGradient, isBackgroundLayerImage, isBackgroundLayerVideo } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { DATA_HS_SECTION_HAS_BG_MEDIA, DND_GRID_AREA_BACKGROUND_CLASS_NAME, DND_GRID_AREA_SECTION_CONTENT_CLASS_NAME, DND_GRID_AREA_CONTAINER_CLASS_NAME, DEFAULT_SECTION_PADDING } from '../constants';

/**
 * Breakpoint names with an image, video, or gradient layer. Renderers join
 * with a space and set as `DATA_HS_SECTION_HAS_BG_MEDIA` on the `<section>`.
 */
export function getBreakpointsWithBackgroundMedia(styles) {
  return Object.keys(styles).filter(breakpointName => {
    var _styles$breakpointNam;
    const layers = (_styles$breakpointNam = styles[breakpointName]) === null || _styles$breakpointNam === void 0 ? void 0 : _styles$breakpointNam.backgroundLayers;
    return layers != null && layers.some(l => isBackgroundLayerImage(l) || isBackgroundLayerVideo(l) || isBackgroundLayerGradient(l));
  });
}
function writeMaxWidthToCssRuleTriple(styles, parentSelector, sectionSelector, breakpointConfigs, ruleTriples) {
  breakpointConfigs.forEach(config => {
    const {
      breakpointName
    } = config;
    const style = styles[breakpointName];
    if (!style) {
      return;
    }
    const selector = buildNodeSelector({
      parentSelector,
      targetSelector: `${sectionSelector}-content`,
      breakpointConfig: config
    });
    if (style.contentWidth) {
      const styleList = [];
      writeMaxWidthToCss(style.contentWidth, styleList);
      const mediaQuery = isMediaQuery(config) ? config.mediaQuery : '';
      ruleTriples.push([selector, styleList.join(' '), mediaQuery]);
    }
  });
  return ruleTriples;
}
function writeSectionStylesToCssHelper(styles, existingStylesByBreakpointName, applyDefaultSectionPadding = true) {
  Object.keys(styles).forEach(breakpointName => {
    const stylesForBreakpoint = existingStylesByBreakpointName[breakpointName] || [];
    const style = styles[breakpointName];
    if (isStyleOverridden(style.isHidden)) {
      stylesForBreakpoint.push(`display: ${style.isHidden ? 'none' : 'block'};`);
    }
    if (isStyleOverridden(style.padding)) {
      stylesForBreakpoint.push(writePadding(style.padding));
    } else if (applyDefaultSectionPadding) {
      stylesForBreakpoint.push(DEFAULT_SECTION_PADDING);
    }
    if (isStyleOverridden(style.margin)) {
      stylesForBreakpoint.push(writeMargin(style.margin));
    }
    existingStylesByBreakpointName[breakpointName] = stylesForBreakpoint;
  });
}
export default function writeSectionStylesToCss(nodeId, styles, breakpointConfigs, dataVariant, applyDefaultSectionPadding = true) {
  const nodeSelector = `.dnd-grid-section.${buildSectionClassname(nodeId)}`;
  const stylesByBreakpointName = {};
  const backgroundLayerStylesByBreakpointName = {};
  const backgroundLayersByBreakpointName = Object.keys(styles).reduce((acc, breakpointName) => {
    const stylesForBreakpoint = styles[breakpointName];
    if (stylesForBreakpoint !== null && stylesForBreakpoint !== void 0 && stylesForBreakpoint.backgroundLayers) {
      acc[breakpointName] = stylesForBreakpoint.backgroundLayers;
    }
    return acc;
  }, {});
  writeBackgroundLayersToCss(nodeId, backgroundLayersByBreakpointName, backgroundLayerStylesByBreakpointName);
  writeSectionStylesToCssHelper(styles, stylesByBreakpointName, applyDefaultSectionPadding);
  const combinedBreakpointStyles = combineBreakpointStyles(breakpointConfigs, stylesByBreakpointName, nodeId, backgroundLayerStylesByBreakpointName, nodeSelector);
  writeMaxWidthToCssRuleTriple(styles, nodeSelector, `.${buildSectionClassname(nodeId)}`, breakpointConfigs, combinedBreakpointStyles);

  // Suppress the variant background (var(--hs-section-backgroundColor) on
  // .dnd-grid-background) where an image/video/gradient layer overlays it.
  // Scoped via the section data-attribute so an SSR'd copy of this rule
  // self-disables when the renderer drops the breakpoint from the attribute.
  // `~=` matches a space-separated token so each breakpoint toggles
  // independently when multiple breakpoints have media at once.
  if (dataVariant) {
    breakpointConfigs.forEach(config => {
      const layers = backgroundLayersByBreakpointName[config.breakpointName];
      if (layers && layers.some(l => isBackgroundLayerImage(l) || isBackgroundLayerVideo(l) || isBackgroundLayerGradient(l))) {
        const mediaQuery = isMediaQuery(config) ? config.mediaQuery : '';
        const scopedSectionSelector = `${nodeSelector}[${DATA_HS_SECTION_HAS_BG_MEDIA}~="${config.breakpointName}"]`;
        const backgroundSelector = buildNodeSelector({
          parentSelector: scopedSectionSelector,
          targetSelector: `> .${DND_GRID_AREA_BACKGROUND_CLASS_NAME}`,
          breakpointConfig: config
        });
        const innerBackgroundSelector = buildNodeSelector({
          parentSelector: `${scopedSectionSelector} > .${DND_GRID_AREA_SECTION_CONTENT_CLASS_NAME} > .${DND_GRID_AREA_CONTAINER_CLASS_NAME}`,
          targetSelector: `> .${DND_GRID_AREA_BACKGROUND_CLASS_NAME}`,
          breakpointConfig: config
        });
        combinedBreakpointStyles.push([`${backgroundSelector}, ${innerBackgroundSelector}`, 'background: transparent;', mediaQuery]);
      }
    });
  }
  return combinedBreakpointStyles;
}